import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  collection,
  getDocs,
  query,
  where,
  doc,
  updateDoc,
  DocumentSnapshot,
  writeBatch,
} from "firebase/firestore";
import { RootState, AppDispatch } from "../../store";
import {
  Participation,
  Tableau,
  TableauFirestore,
  Tour,
  Tournoi,
} from "../../types";
import firestoreInstance from "../../services/firestoreInstance";
import { fetchParticipationsWithPlayers } from "../../helpers/fetchParticipationsWithPlayer";

export type TournoiState = {
  tournoi: Tournoi | null;
  boards: Tableau[];
  boardRegistrations: Tableau[];
  boardRegistrationsLoading: boolean;
  rounds: Tour[];
  allRegistrations: Participation[];
  allRegistrationsLoading: boolean;
};

const initialState: TournoiState = {
  tournoi: null,
  boards: [],
  boardRegistrations: [],
  boardRegistrationsLoading: false,
  rounds: [],
  allRegistrations: [],
  allRegistrationsLoading: true,
};

// migré
export const fetchTournoi = createAsyncThunk(
  "tournoi/fetchTournoi",
  async () => {
    const querySnapshot = await getDocs(
      query(
        collection(firestoreInstance, "tournois"),
        where("current", "==", true)
      )
    );
    const doc = querySnapshot.docs[0];

    return { ...(doc.data() as Tournoi), ...{ id: doc.id } };
  }
);

export const fetchBoards = createAsyncThunk<
  Tableau[],
  void,
  { state: RootState }
>("tournoi/fetchBoards", async (_, thunkApi) => {
  const currentTournamentId = thunkApi.getState().tournoi.tournoi?.id;

  const snapshot = await getDocs(
    collection(firestoreInstance, "tournois", currentTournamentId!, "tableaux")
  );

  const tableaux = snapshot.docs.map((doc: DocumentSnapshot) => {
    const data = doc.data() as TableauFirestore;

    const tableau: Tableau = {
      ...(data as Omit<Tableau, "id" | "date" | "isFull">),
      id: doc.id,
      date: data.date.toDate(), // ✅ conversion Timestamp → Date
      isFull: data.nbParticipants >= (data.realNbPlaces ?? data.nbPlace),
    };

    return tableau;
  });

  const tabs = tableaux.sort((a, b) => {
    const dateA =
      a.date instanceof Date ? a.date.getTime() : new Date(a.date).getTime();
    const dateB =
      b.date instanceof Date ? b.date.getTime() : new Date(b.date).getTime();

    if (dateA !== dateB) {
      return dateA - dateB;
    }

    return a.name.localeCompare(b.name);
  });

  return tabs;
});

export const fetchRounds = createAsyncThunk<Tour[], void, { state: RootState }>(
  "tournoi/fetchRounds",
  async (_, thunkApi) => {
    const tours = (
      await getDocs(query(collection(firestoreInstance, "tour")))
    ).docs.map((doc) => ({ ...(doc.data() as Tour), ...{ id: doc.id } }));
    return tours;
  }
);

export const fetchBoardRegistrations = createAsyncThunk<
  Tableau[],
  { forceRefresh?: boolean } | void,
  { state: RootState }
>("tournoi/fetchBoardRegistrations", async (params, { getState }) => {
  const db = firestoreInstance;
  const tournamentId = getState().tournoi.tournoi?.id;
  if (!tournamentId) throw new Error("Tournoi courant introuvable.");

  // Charger les tableaux
  const tableauxSnap = await getDocs(
    collection(db, "tournois", tournamentId, "tableaux")
  );
  const tableauxMap = new Map(
    tableauxSnap.docs.map((doc) => [
      doc.id,
      {
        id: doc.id,
        ...doc.data(),
        date: doc.data().date?.toDate(),
        participation: [] as Participation[],
      },
    ])
  );

  // Charger les participations enrichies avec rechargement forcé si demandé
  const forceRefresh =
    params && typeof params === "object" ? params.forceRefresh : false;
  const participations = await fetchParticipationsWithPlayers(forceRefresh);

  // Associer chaque participation à son tableau
  for (const part of participations) {
    const tableau = tableauxMap.get(part.tableauId);
    if (!tableau) continue;
    tableau.participation.push(part);
  }

  // Trier participations et tableaux
  const tableaux = Array.from(tableauxMap.values()).map(
    (t) =>
      ({
        ...t,
        participation: t.participation.sort(
          (a, b) => a.dateInscription.getTime() - b.dateInscription.getTime()
        ),
      } as Tableau)
  );

  return tableaux.sort((a, b) => a.name.localeCompare(b.name));
});

export const fetchAllRegistrations = createAsyncThunk<
  Participation[],
  { reload: boolean } | undefined,
  { state: RootState }
>("tournoi/fetchAllRegistrations", async (params, { getState }) => {
  const forceRefresh = params?.reload || false;
  console.log("fetchAllRegistrations - forceRefresh:", forceRefresh);

  const participations = await fetchParticipationsWithPlayers(forceRefresh);
  console.log(
    "fetchAllRegistrations - participations récupérées:",
    participations.length
  );

  const sortedParticipations = participations.sort(
    (a, b) => a.dateInscription.getTime() - b.dateInscription.getTime()
  );

  console.log(
    "fetchAllRegistrations - participations triées:",
    sortedParticipations.length
  );
  return sortedParticipations;
});

export const updateNbPlaces = createAsyncThunk<
  void,
  { boardId: string; nbPlace: string },
  { state: RootState }
>(
  "tournoi/updateBoardNbPlaces",
  async ({ boardId, nbPlace }, { dispatch, getState }) => {
    const currentTournamentId: string = getState().tournoi.tournoi?.id;
    const tournamentRef = doc(
      firestoreInstance,
      "tournois",
      currentTournamentId,
      "tableaux",
      boardId
    );

    await updateDoc(tournamentRef, { nbPlace });
    await dispatch(fetchBoardRegistrations());
    return;
  }
);

export const updateRealNbPlaces = createAsyncThunk<
  void,
  { boardId: string; realNbPlaces: number },
  { state: RootState }
>(
  "tournoi/updateRealNbPlaces",
  async ({ boardId, realNbPlaces }, { dispatch, getState }) => {
    const currentTournamentId: string = getState().tournoi.tournoi?.id;
    const tournamentRef = doc(
      firestoreInstance,
      "tournois",
      currentTournamentId,
      "tableaux",
      boardId
    );

    await updateDoc(tournamentRef, { realNbPlaces });
    await dispatch(fetchBoardRegistrations());
    return;
  }
);

export const changeBoardStatus = createAsyncThunk<
  void,
  { boardId: string; closed: boolean },
  { state: RootState }
>(
  "tournoi/changeBoardStatus",
  async ({ boardId, closed }, { dispatch, getState }) => {
    const currentTournamentId: string = getState().tournoi.tournoi?.id;
    const tournamentRef = doc(
      firestoreInstance,
      "tournois",
      currentTournamentId,
      "tableaux",
      boardId
    );

    await updateDoc(tournamentRef, { ferme: closed });
    await dispatch(fetchBoardRegistrations());
    return;
  }
);

export const changeBoardQueueStatus = createAsyncThunk<
  void,
  { boardId: string; hasWaitingQueue: boolean },
  { state: RootState }
>(
  "tournoi/changeBoardQueueStatus",
  async ({ boardId, hasWaitingQueue }, { dispatch, getState }) => {
    const currentTournamentId: string = getState().tournoi.tournoi?.id;
    const tournamentRef = doc(
      firestoreInstance,
      "tournois",
      currentTournamentId,
      "tableaux",
      boardId
    );

    await updateDoc(tournamentRef, { hasWaitingQueue });
    await dispatch(fetchBoardRegistrations());
    return;
  }
);

export const changeTournamentRegistrationStatus = createAsyncThunk<
  void,
  { tournamentId: string; isRegisterClosed: boolean },
  { dispatch: AppDispatch }
>(
  "tournoi/changeTournamentRegistrationStatus",
  async ({ tournamentId, isRegisterClosed }, { dispatch }) => {
    //mettre à jour le champs isRegisterClosed du document de la collection firestore tournois dont l'id vaut tournamentId

    const tournamentRef = doc(firestoreInstance, "tournois", tournamentId);

    await updateDoc(tournamentRef, { isRegisterClosed });

    await dispatch(fetchTournoi());

    return;
  }
);

export const changeTournamentBoardClosedLabel = createAsyncThunk<
  void,
  { tournamentId: string; boardClosedDefaultLabel: string },
  { dispatch: AppDispatch }
>(
  "tournoi/changeTournamentBoardClosedLabel",
  async ({ tournamentId, boardClosedDefaultLabel }, { dispatch }) => {
    const tournamentRef = doc(firestoreInstance, "tournois", tournamentId);

    await updateDoc(tournamentRef, { boardClosedDefaultLabel });

    await dispatch(fetchTournoi());

    return;
  }
);

export const changeTournamentBoardClosedLabelWithQueue = createAsyncThunk<
  void,
  { tournamentId: string; boardClosedWithQueueLabel: string },
  { dispatch: AppDispatch }
>(
  "tournoi/changeTournamentBoardClosedLabelWithQueue",
  async ({ tournamentId, boardClosedWithQueueLabel }, { dispatch }) => {
    const tournamentRef = doc(firestoreInstance, "tournois", tournamentId);

    await updateDoc(tournamentRef, { boardClosedWithQueueLabel });

    await dispatch(fetchTournoi());

    return;
  }
);

export const changeClosedTournamentLabel = createAsyncThunk<
  void,
  { tournamentId: string; closedTournamentLabel: string },
  { dispatch: AppDispatch }
>(
  "tournoi/closedTournamentLabel",
  async ({ tournamentId, closedTournamentLabel }, { dispatch }) => {
    const tournamentRef = doc(firestoreInstance, "tournois", tournamentId);

    await updateDoc(tournamentRef, { closedTournamentLabel });

    await dispatch(fetchTournoi());

    return;
  }
);

export const changeParticipationPaymentStatus = createAsyncThunk<
  any,
  { participationId: string; status: string },
  { state: RootState }
>(
  "player/changeParticipationPaymentStatus",
  async ({ participationId, status }, { getState, dispatch }) => {
    // le champ paymentStatus est dans le document d'id "data" de la sous-collection "private" de la collection participation
    await updateDoc(
      doc(
        firestoreInstance,
        "participation",
        participationId,
        "private",
        "data"
      ),
      { paymentStatus: status }
    );

    await dispatch(fetchAllRegistrations({ reload: true }));
    return;
  }
);

export const changeTournamentStatsCollection = createAsyncThunk<
  void,
  { tournamentId: string; statsCollectionName: string },
  { dispatch: AppDispatch }
>(
  "tournoi/changeTournamentStatsCollection",
  async ({ tournamentId, statsCollectionName }, { dispatch }) => {
    const tournamentRef = doc(firestoreInstance, "tournois", tournamentId);
    await updateDoc(tournamentRef, { statsCollectionName });
    await dispatch(fetchTournoi());
    return;
  }
);

export const changeTournamentAlertThresholds = createAsyncThunk<
  void,
  { tournamentId: string; alertThresholds: Tournoi["alertThresholds"] },
  { dispatch: AppDispatch }
>(
  "tournoi/changeTournamentAlertThresholds",
  async ({ tournamentId, alertThresholds }, { dispatch }) => {
    const tournamentRef = doc(firestoreInstance, "tournois", tournamentId);
    await updateDoc(tournamentRef, { alertThresholds });
    await dispatch(fetchTournoi());
    return;
  }
);

// 🔥 Gestion des dossards

// Fonction de tri pour les dossards : points décroissants, puis nom/prénom alphabétique
const sortPlayersForDossards = (a: any, b: any) => {
  // 1. Tri par points décroissants
  const pointsA = parseInt(a.points) || 0;
  const pointsB = parseInt(b.points) || 0;

  if (pointsA !== pointsB) {
    return pointsB - pointsA; // Décroissant
  }

  // 2. Si points égaux, tri par nom alphabétique
  const nameA = a.name?.toLowerCase() || "";
  const nameB = b.name?.toLowerCase() || "";

  if (nameA !== nameB) {
    return nameA.localeCompare(nameB);
  }

  // 3. Si noms égaux, tri par prénom alphabétique
  const surnameA = a.surname?.toLowerCase() || "";
  const surnameB = b.surname?.toLowerCase() || "";

  return surnameA.localeCompare(surnameB);
};

export const generateDossards = createAsyncThunk<
  void,
  void,
  { state: RootState; dispatch: AppDispatch }
>(
  "tournoi/generateDossards",
  async (_, { getState, dispatch, rejectWithValue }) => {
    try {
      const participations = await fetchParticipationsWithPlayers();

      if (!participations || participations.length === 0) {
        throw new Error("Aucune inscription trouvée dans le tournoi");
      }

      // Récupérer tous les joueurs uniques qui ont au moins une inscription
      const uniquePlayers = new Map();
      participations.forEach((p) => {
        // Vérifier que le joueur existe et a un ID valide
        if (p.playersId && p.players && p.players.id) {
          if (!uniquePlayers.has(p.playersId)) {
            uniquePlayers.set(p.playersId, p.players);
          }
        }
      });

      if (uniquePlayers.size === 0) {
        throw new Error("Aucun joueur valide trouvé dans les inscriptions");
      }

      // Trier les joueurs par points décroissants, puis nom/prénom alphabétique
      const sortedPlayers = Array.from(uniquePlayers.values())
        .filter((player) => player && player.name && player.surname) // Filtrer les joueurs avec des données complètes
        .sort(sortPlayersForDossards);

      if (sortedPlayers.length === 0) {
        throw new Error("Aucun joueur avec des données complètes trouvé");
      }

      // Trouver le dernier dossard attribué
      let nextDossard = 1;
      for (const player of sortedPlayers) {
        if (
          player.dossard &&
          typeof player.dossard === "number" &&
          player.dossard >= nextDossard
        ) {
          nextDossard = player.dossard + 1;
        }
      }

      // Vérifier s'il y a des joueurs sans dossard
      const playersWithoutDossard = sortedPlayers.filter(
        (p) => !p.dossard || typeof p.dossard !== "number"
      );
      if (playersWithoutDossard.length === 0) {
        throw new Error("Tous les joueurs ont déjà un dossard");
      }

      // Attribuer des dossards aux joueurs qui n'en ont pas
      const batch = writeBatch(firestoreInstance);
      for (const player of sortedPlayers) {
        if (!player.dossard || typeof player.dossard !== "number") {
          const playerRef = doc(firestoreInstance, "players", player.id);
          batch.update(playerRef, { dossard: nextDossard++ });
        }
      }

      await batch.commit();

      // Forcer le rechargement des données
      await dispatch(fetchAllRegistrations({ reload: true }));
      await dispatch(fetchBoardRegistrations({ forceRefresh: true }));
    } catch (error) {
      console.error("Erreur dans generateDossards:", error);
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : "Erreur lors de la génération des dossards"
      );
    }
  }
);

export const regenerateAllDossards = createAsyncThunk<
  void,
  void,
  { state: RootState; dispatch: AppDispatch }
>(
  "tournoi/regenerateAllDossards",
  async (_, { getState, dispatch, rejectWithValue }) => {
    try {
      const participations = await fetchParticipationsWithPlayers();

      if (!participations || participations.length === 0) {
        throw new Error("Aucune inscription trouvée dans le tournoi");
      }

      // Récupérer tous les joueurs uniques qui ont au moins une inscription
      const uniquePlayers = new Map();
      participations.forEach((p) => {
        // Vérifier que le joueur existe et a un ID valide
        if (p.playersId && p.players && p.players.id) {
          if (!uniquePlayers.has(p.playersId)) {
            uniquePlayers.set(p.playersId, p.players);
          }
        }
      });

      if (uniquePlayers.size === 0) {
        throw new Error("Aucun joueur valide trouvé dans les inscriptions");
      }

      // Trier les joueurs par points décroissants, puis nom/prénom alphabétique
      const sortedPlayers = Array.from(uniquePlayers.values())
        .filter((player) => player && player.name && player.surname) // Filtrer les joueurs avec des données complètes
        .sort(sortPlayersForDossards);

      if (sortedPlayers.length === 0) {
        throw new Error("Aucun joueur avec des données complètes trouvé");
      }

      // Attribuer des dossards à tous les joueurs (en commençant par 1)
      const batch = writeBatch(firestoreInstance);
      let dossard = 1;
      for (const player of sortedPlayers) {
        const playerRef = doc(firestoreInstance, "players", player.id);
        batch.update(playerRef, { dossard: dossard++ });
      }

      await batch.commit();
      await dispatch(fetchAllRegistrations({ reload: true }));
      await dispatch(fetchBoardRegistrations({ forceRefresh: true }));
    } catch (error) {
      console.error("Erreur dans regenerateAllDossards:", error);
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : "Erreur lors de la régénération des dossards"
      );
    }
  }
);

export const clearAllDossards = createAsyncThunk<
  void,
  void,
  { state: RootState; dispatch: AppDispatch }
>(
  "tournoi/clearAllDossards",
  async (_, { getState, dispatch, rejectWithValue }) => {
    try {
      const participations = await fetchParticipationsWithPlayers();

      if (!participations || participations.length === 0) {
        throw new Error("Aucune inscription trouvée dans le tournoi");
      }

      // Récupérer tous les joueurs uniques qui ont au moins une inscription
      const uniquePlayers = new Map();
      participations.forEach((p) => {
        // Vérifier que le joueur existe et a un ID valide
        if (p.playersId && p.players && p.players.id) {
          if (!uniquePlayers.has(p.playersId)) {
            uniquePlayers.set(p.playersId, p.players);
          }
        }
      });

      if (uniquePlayers.size === 0) {
        throw new Error("Aucun joueur valide trouvé dans les inscriptions");
      }

      // Vérifier s'il y a des joueurs avec dossard
      const playersWithDossard = Array.from(uniquePlayers.values()).filter(
        (p) => p && p.dossard && typeof p.dossard === "number"
      );
      if (playersWithDossard.length === 0) {
        throw new Error("Aucun dossard à supprimer");
      }

      // Supprimer tous les dossards
      const batch = writeBatch(firestoreInstance);
      for (const player of Array.from(uniquePlayers.values())) {
        if (player && player.dossard && typeof player.dossard === "number") {
          const playerRef = doc(firestoreInstance, "players", player.id);
          batch.update(playerRef, { dossard: null });
        }
      }

      await batch.commit();
      await dispatch(fetchAllRegistrations({ reload: true }));
      await dispatch(fetchBoardRegistrations({ forceRefresh: true }));
    } catch (error) {
      console.error("Erreur dans clearAllDossards:", error);
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : "Erreur lors de la suppression des dossards"
      );
    }
  }
);

// Nouvelle action pour générer un fichier CSV des joueurs inscrits
export const generatePlayersCSV = createAsyncThunk<
  void,
  void,
  { state: RootState; dispatch: AppDispatch }
>(
  "tournoi/generatePlayersCSV",
  async (_, { getState, dispatch, rejectWithValue }) => {
    try {
      const participations = await fetchParticipationsWithPlayers();

      if (!participations || participations.length === 0) {
        throw new Error("Aucune inscription trouvée dans le tournoi");
      }

      // Récupérer tous les joueurs uniques qui ont au moins une inscription
      const uniquePlayers = new Map();
      participations.forEach((p) => {
        // Vérifier que le joueur existe et a un ID valide
        if (p.playersId && p.players && p.players.id) {
          if (!uniquePlayers.has(p.playersId)) {
            uniquePlayers.set(p.playersId, p.players);
          }
        }
      });

      if (uniquePlayers.size === 0) {
        throw new Error("Aucun joueur valide trouvé dans les inscriptions");
      }

      // Vérifier que tous les joueurs ont un dossard
      const playersWithDossard = Array.from(uniquePlayers.values()).filter(
        (p) => p && p.dossard && typeof p.dossard === "number"
      );

      if (playersWithDossard.length !== uniquePlayers.size) {
        throw new Error(
          "Tous les joueurs doivent avoir un dossard pour générer le CSV"
        );
      }

      // Trier les joueurs par dossard
      const sortedPlayers = playersWithDossard.sort(
        (a, b) => a.dossard - b.dossard
      );

      // Créer le contenu CSV
      const csvHeader = "Dossard,Numéro de licence,Nom,Prénom\n";
      const csvContent = sortedPlayers
        .map((player) => {
          const nom = player.name || "";
          const prenom = player.surname || "";
          const licenceId = player.licenceId || "";
          return `${player.dossard},"${licenceId}","${nom}","${prenom}"`;
        })
        .join("\n");

      const csvData = csvHeader + csvContent;

      // Créer et télécharger le fichier
      const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", "joueurs_inscrits.csv");
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Erreur dans generatePlayersCSV:", error);
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : "Erreur lors de la génération du fichier CSV"
      );
    }
  }
);

// Nouvelle action pour clôturer la phase d'inscription
export const closeRegistrationPhase = createAsyncThunk<
  void,
  void,
  { state: RootState; dispatch: AppDispatch }
>(
  "tournoi/closeRegistrationPhase",
  async (_, { getState, dispatch, rejectWithValue }) => {
    try {
      const currentTournamentId = getState().tournoi.tournoi?.id;
      if (!currentTournamentId) {
        throw new Error("Tournoi courant introuvable");
      }

      // Mettre à jour le tournoi pour indiquer que la phase d'inscription est clôturée
      const tournamentRef = doc(
        firestoreInstance,
        "tournois",
        currentTournamentId
      );
      await updateDoc(tournamentRef, { registrationPhaseClosed: true });

      // Recharger les données du tournoi
      await dispatch(fetchTournoi());
    } catch (error) {
      console.error("Erreur dans closeRegistrationPhase:", error);
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : "Erreur lors de la clôture de la phase d'inscription"
      );
    }
  }
);

const tournoiSlice = createSlice({
  name: "tournoi",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(
        fetchTournoi.fulfilled,
        (state: TournoiState, action: PayloadAction<Tournoi>) => {
          state.tournoi = action.payload;
        }
      )
      .addCase(
        fetchBoards.fulfilled,
        (state: TournoiState, action: PayloadAction<Tableau[]>) => {
          state.boards = action.payload;
          //.sort((a, b) =>
          //   a.name.localeCompare(b.name)
          // );
        }
      )
      .addCase(
        fetchBoardRegistrations.fulfilled,
        (state: TournoiState, action: PayloadAction<Tableau[]>) => {
          state.boardRegistrations = action.payload.sort((a, b) =>
            a.name.localeCompare(b.name)
          );
          state.boardRegistrationsLoading = false;
        }
      )
      .addCase(fetchBoardRegistrations.pending, (state: TournoiState) => {
        state.boardRegistrationsLoading = true;
        state.boardRegistrations = [];
      })
      .addCase(
        fetchRounds.fulfilled,
        (state: TournoiState, action: PayloadAction<Tour[]>) => {
          state.rounds = action.payload.sort((a, b) => a.ordre - b.ordre);
        }
      )
      .addCase(
        fetchAllRegistrations.fulfilled,
        (state: TournoiState, action: PayloadAction<Participation[]>) => {
          const boardsMap = new Map(state.boards.map((b) => [b.id, b]));

          state.allRegistrations = action.payload
            .map((p) => {
              const tableau = boardsMap.get(p.tableauId);
              // Si pas de tableau dans le state, on garde quand même la participation
              // Le tableau sera chargé plus tard ou on utilisera les données de base
              return { ...p, tableau: tableau || p.tableau };
            })
            .filter((p): p is Participation => p !== null);

          state.allRegistrationsLoading = false;

          console.log(
            "fetchAllRegistrations.fulfilled - données stockées:",
            state.allRegistrations.length
          );
        }
      )
      .addCase(fetchAllRegistrations.pending, (state: TournoiState, action) => {
        const { meta } = action;
        if (meta && meta.arg && meta.arg.reload) {
          return;
        }
        // Reset registrations only if reload is not requested
        state.allRegistrationsLoading = true;
        state.allRegistrations = [];
      })
      .addCase(
        changeTournamentStatsCollection.fulfilled,
        (state: TournoiState, action: PayloadAction<void>) => {
          // Le state est déjà mis à jour par fetchTournoi
        }
      )
      // Gestion des erreurs pour les dossards
      .addCase(generateDossards.rejected, (state: TournoiState, action) => {
        console.error(
          "Erreur lors de la génération des dossards:",
          action.payload
        );
      })
      .addCase(
        regenerateAllDossards.rejected,
        (state: TournoiState, action) => {
          console.error(
            "Erreur lors de la régénération des dossards:",
            action.payload
          );
        }
      )
      .addCase(clearAllDossards.rejected, (state: TournoiState, action) => {
        console.error(
          "Erreur lors de la suppression des dossards:",
          action.payload
        );
      });
  },
});

export default tournoiSlice.reducer;

export const getTournoi = (state: RootState): Tournoi => state.tournoi.tournoi!;

export const getAreOpenedRegistrations = (state: RootState) => {
  const isAdmin = state.session.admin;
  const { isRegisterClosed, isRegisterClosedAdmin } = state.tournoi.tournoi!;

  console.log("🏠 [DEBUG] getAreOpenedRegistrations:");
  console.log("🏠 [DEBUG] - isAdmin:", isAdmin);
  console.log("🏠 [DEBUG] - isRegisterClosed:", isRegisterClosed);
  console.log("🏠 [DEBUG] - isRegisterClosedAdmin:", isRegisterClosedAdmin);

  const result = isAdmin ? !isRegisterClosedAdmin : !isRegisterClosed;
  console.log("🏠 [DEBUG] - Résultat:", result);

  return result;
};

export const getBoards = (state: RootState) => state.tournoi.boards;

export const getBoardsSortedByName = (state: RootState) => {
  return [...state.tournoi.boards].sort((a, b) => a.name.localeCompare(b.name));
};

export const getRounds = (state: RootState) => state.tournoi.rounds;

export const getBoardRegistrations = (state: RootState) =>
  state.tournoi.boardRegistrations;

export const getBoardRegistrationsLoading = (state: RootState) =>
  state.tournoi.boardRegistrationsLoading;

export const getAllRegistrations = (state: RootState) =>
  state.tournoi.allRegistrations;

export const getAllRegistrationsLoading = (state: RootState) =>
  state.tournoi.allRegistrationsLoading;

export const getLiveRefreshPeriodInSeconds = (state: RootState) =>
  state.tournoi.tournoi?.liveRefreshPeriodInSeconds
    ? state.tournoi.tournoi?.liveRefreshPeriodInSeconds * 1000
    : 10000;

// Nouveau sélecteur pour vérifier si tous les joueurs ont un dossard
export const getAreAllDossardsGenerated = (state: RootState) => {
  const participations = state.tournoi.allRegistrations;
  console.log(
    "getAreAllDossardsGenerated - participations:",
    participations.length
  );

  if (participations.length === 0) {
    console.log(
      "getAreAllDossardsGenerated - aucune participation, retour false"
    );
    return false;
  }

  // Récupérer tous les joueurs uniques
  const uniquePlayers = new Map();
  participations.forEach((p) => {
    if (p.playersId && p.players && p.players.id) {
      if (!uniquePlayers.has(p.playersId)) {
        uniquePlayers.set(p.playersId, p.players);
      }
    }
  });

  console.log(
    "getAreAllDossardsGenerated - joueurs uniques:",
    uniquePlayers.size
  );

  // Vérifier que tous les joueurs ont un dossard
  const allHaveDossard = Array.from(uniquePlayers.values()).every(
    (p) => p && p.dossard && typeof p.dossard === "number"
  );

  console.log("getAreAllDossardsGenerated - tous ont dossard:", allHaveDossard);

  // Log des joueurs sans dossard pour debug
  const playersWithoutDossard = Array.from(uniquePlayers.values()).filter(
    (p) => !p || !p.dossard || typeof p.dossard !== "number"
  );
  if (playersWithoutDossard.length > 0) {
    console.log(
      "getAreAllDossardsGenerated - joueurs sans dossard:",
      playersWithoutDossard.map((p) => ({
        id: p?.id,
        nom: p?.name,
        prenom: p?.surname,
        dossard: p?.dossard,
      }))
    );
  }

  return allHaveDossard;
};

// Nouveau sélecteur pour vérifier si la phase d'inscription est clôturée
export const getIsRegistrationPhaseClosed = (state: RootState) => {
  return state.tournoi.tournoi?.registrationPhaseClosed || false;
};
